/**
 * @copyright Copyright 2024-2025 Epic Systems Corporation
 * @file Generic helper functions for the Connectors Management UI
 * @module Epic.AppOrchard.ConnectorManagement.Helpers
 */

import {
	ICategoryForConnectorApp,
	IConnectorManagementAlias,
	ISqlRecord,
} from "ao/types/connectorManagement";
import { getDate } from "ao/utils/helpers";
import { UseFormReturn } from "react-hook-form";
import {
	AliasForDefaultNameId,
	ConnectorManagementActions,
	ManagementTabs,
	SuccessToastVerbs,
} from "./data/constants";

export function getTabSpecificText(tab: ManagementTabs): string {
	if (tab === ManagementTabs.Apps) {
		return "App";
	}
	if (tab === ManagementTabs.Organizations) {
		return "Organization";
	} else {
		return "";
	}
}

export function getEditToastSuccessDescription(action: ConnectorManagementActions, recordId: number) {
	return `App ${recordId} ${SuccessToastVerbs[action]} successfully.`;
}

/**
 * Combines the source and target aliases, removing any duplicates. Also adds the source name as the first alias.
 * @param sourceAliases The source aliases.
 * @param targetAliases The target aliases.
 * @param sourceName The source name.
 * @returns A list of aliases.
 */
export function combineConnectorManagementAliasesForMerge(
	sourceAliases: IConnectorManagementAlias[] | undefined,
	targetAliases: IConnectorManagementAlias[] | undefined,
	sourceName: string | undefined,
): IConnectorManagementAlias[] {
	return filterAndCombineArrays(
		[
			sourceName
				? [
						{
							id: AliasForDefaultNameId,
							sortOrder: 1,
							alias: sourceName,
							comment: `Merged on ${new Date().toDateString()}`,
						},
				  ]
				: [],
			sourceAliases ?? [],
			targetAliases ?? [],
		],
		(alias, index, self) =>
			index === self.findIndex((ali) => ali.alias === alias.alias && ali.comment === alias.comment),
	);
}

/**
 * Combines the source and target categories, removing any duplicates.
 * @param sourceCategories The source categories.
 * @param targetCategories The target categories.
 * @returns A list of categories.
 */
export function combineConnectorManagementCategories(
	sourceCategories: ICategoryForConnectorApp[] | undefined,
	targetCategories: ICategoryForConnectorApp[] | undefined,
): ICategoryForConnectorApp[] {
	const cats = filterAndCombineArrays(
		[sourceCategories ?? [], targetCategories ?? []],
		(category, index, self) => index === self.findIndex((cat) => cat.id === category.id),
	);
	return cats;
}

/**
 * Combines multiple arrays into a single array and filters the result based on a predicate.
 * @param arrays The arrays to combine.
 * @param predicate The predicate to use for filtering.
 * @returns The combined and filtered array.
 */
export function filterAndCombineArrays<T>(
	arrays: T[][],
	predicate: (item: T, index: number, self: T[]) => boolean,
): T[] {
	var combinedArray: T[] = [];
	arrays.forEach((array) => {
		combinedArray.push(...array);
	});
	combinedArray = combinedArray.filter(predicate);
	return combinedArray;
}

/**
 * Builds a list of categories from the source and target categories.
 * @param sourceCategories The source categories.
 * @param targetCategories The target categories.
 * @returns A list of categories.
 */
export function buildCategories(
	sourceCategories: ISqlRecord[] | undefined,
	targetCategories: ISqlRecord[] | undefined,
): ISqlRecord[] {
	var categories: ISqlRecord[] = [];
	if (sourceCategories) {
		categories.push(...sourceCategories);
	}
	if (targetCategories) {
		categories.push(...targetCategories);
	}
	categories = categories.filter(
		(category, index, self) => index === self.findIndex((cat) => cat.id === category.id),
	);
	return categories;
}

/**
 * Splits an array into two arrays based on a predicate.
 * @param array The array to split.
 * @param predicate The predicate to use for splitting.
 * @returns An array with two elements. The first element contains the items that satisfy the predicate, and the second element contains the items that do not
 */
export function splitArray<T>(array: T[], predicate: (item: T) => boolean): [truthy: T[], falsy: T[]] {
	const truthy: T[] = [];
	const falsy: T[] = [];
	array.forEach((item) => {
		if (predicate(item)) {
			truthy.push(item);
		} else {
			falsy.push(item);
		}
	});
	return [truthy, falsy];
}
/**
 * Removes all spaces from a string.
 * @param string The string to remove spaces from.
 * @returns The string with all spaces removed.
 */
export function removeSpaces(string: string): string {
	return string.replace(/\s+/g, "");
}

export function handleAliasUpdateOnNameChange(
	userDisplayName: string,
	methods: UseFormReturn<any>,
	aliases: IConnectorManagementAlias[],
	event: React.ChangeEvent<HTMLInputElement>,
): void {
	const { control, trigger, setValue } = methods;
	setValue("name", event.currentTarget.value, {
		shouldValidate: true,
		shouldDirty: true,
		shouldTouch: true,
	});

	const defaultName: string = control._defaultValues.name ?? "";

	if (!defaultName) {
		return;
	}

	const nameIsDirty: boolean = event?.currentTarget.value.trim() !== defaultName;

	if (nameIsDirty === aliases?.some(({ alias }) => alias === defaultName)) {
		trigger("aliases");
		return;
	}

	if (!nameIsDirty) {
		const filteredAliases: IConnectorManagementAlias[] = aliases.filter(
			({ id }) => id !== AliasForDefaultNameId,
		);
		const sortedAliases: IConnectorManagementAlias[] = filteredAliases.map((alias) => {
			return { ...alias, sortOrder: alias.sortOrder - 1 };
		});
		setValue("aliases", sortedAliases, {
			shouldValidate: true,
		});
		return;
	}

	const defaultNameAlias: IConnectorManagementAlias = {
		id: AliasForDefaultNameId,
		sortOrder: 1,
		alias: defaultName,
		comment: `Name changed on ${getDate(new Date())} by ${userDisplayName}`,
	};

	const sortedAliases = aliases.map(
		(alias): IConnectorManagementAlias => {
			return { ...alias, sortOrder: alias.sortOrder + 1 };
		},
	);

	setValue("aliases", [defaultNameAlias, ...sortedAliases], {
		shouldValidate: true,
	});
}
